/**
 * Build `filters` object for POST /billing/exports from SaaS Module Billing UI state.
 *
 * @param {object} uiFilters - SaasBilling filter state (`module` = SaaS module catalog id, or legacy display name)
 * @param {Array<object>} listData - Rows from GET /billing/saas-module (for resolving clientId when a client is selected)
 * @param {Array<{ id: string, name: string }>} moduleCatalog - SaaS modules (id + display name)
 * @param {Array<object>} [clientsList] - From GET /clients (optional; resolves clientId when listData has no row)
 */
export function toYmd(value) {
  if (value == null || value === "") return null;
  if (typeof value === "string") return value.split("T")[0];
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }
  return String(value).slice(0, 10);
}

/**
 * Normalize date for GET query: YYYY-MM-DD, or keep ISO string when already includes `T`.
 * @param {unknown} value
 * @returns {string|null}
 */
export function toApiDateQueryParam(value) {
  if (value == null || value === "") return null;
  if (typeof value === "string") {
    const t = value.trim();
    if (t.includes("T")) return t;
    return toYmd(t);
  }
  return toYmd(value);
}

/**
 * GET /billing/saas-module — server-side list filters (query string).
 *
 * @param {object} uiFilters - SaasBilling filter state (`module` = catalog id, empty = all; legacy name still accepted)
 * @param {object} [options]
 * @param {Array<object>} [options.clientsList] - From GET /clients (name → clientId)
 * @param {Array<object>} [options.listData] - SaaS rows fallback when client not in clients list
 * @param {Array<{ id: string, name: string }>} [options.moduleCatalog]
 * @returns {Record<string, string>} Query keys may include `billingType` when not "Both" (if the API supports it).
 */
export function buildSaasModuleListQueryParams(
  uiFilters,
  { clientsList = [], listData = [], moduleCatalog = [] } = {}
) {
  const q = {};

  if (uiFilters?.status != null && String(uiFilters.status).trim() !== "") {
    q.status = String(uiFilters.status).trim();
  }

  if (uiFilters?.client && uiFilters.client !== "All Clients") {
    const name = String(uiFilters.client).trim();
    const fromClient = clientsList.find(
      (c) => String(c.clientName ?? c.name ?? "").trim() === name
    );
    const cid = fromClient?.id ?? fromClient?.clientId;
    if (cid != null && String(cid).trim() !== "") {
      q.clientId = String(cid).trim();
    } else {
      const row = listData.find((r) => String(r.clientName ?? "").trim() === name);
      if (row?.clientId != null && String(row.clientId).trim() !== "") {
        q.clientId = String(row.clientId).trim();
      }
    }
  }

  const moduleKey = uiFilters?.module != null ? String(uiFilters.module).trim() : "";
  /** UI stores module catalog id; legacy name still accepted. */
  if (moduleKey !== "" && moduleKey !== "All Modules") {
    const byId = moduleCatalog.find((m) => String(m.id).trim() === moduleKey);
    if (byId?.id != null && String(byId.id).trim() !== "") {
      q.moduleId = String(byId.id).trim();
    } else {
      const byName = moduleCatalog.find((m) => String(m.name ?? "").trim() === moduleKey);
      if (byName?.id != null && String(byName.id).trim() !== "") {
        q.moduleId = String(byName.id).trim();
      }
    }
  }

  const dr = uiFilters?.dateRange || {};
  const start = toApiDateQueryParam(dr.startDate);
  const end = toApiDateQueryParam(dr.endDate);
  if (start && end) {
    q.startDate = start;
    q.endDate = end;
  }

  if (uiFilters?.billingType && uiFilters.billingType !== "Both") {
    q.billingType = String(uiFilters.billingType).trim();
  }

  return q;
}

export function buildSaasBillingExportFilters(
  uiFilters,
  listData = [],
  moduleCatalog = [],
  clientsList = []
) {
  const filters = {};

  if (uiFilters?.status) {
    filters.status = uiFilters.status;
  }

  if (uiFilters?.client && uiFilters.client !== "All Clients") {
    const name = String(uiFilters.client).trim();
    const fromClient = clientsList.find(
      (c) => String(c.clientName ?? c.name ?? "").trim() === name
    );
    const cid = fromClient?.id ?? fromClient?.clientId;
    if (cid != null && String(cid).trim() !== "") {
      filters.clientId = String(cid).trim();
    } else {
      const row = listData.find((r) => String(r.clientName ?? "").trim() === name);
      if (row?.clientId) {
        filters.clientId = String(row.clientId);
      }
    }
  }

  const moduleKey = uiFilters?.module != null ? String(uiFilters.module).trim() : "";
  if (moduleKey !== "" && moduleKey !== "All Modules") {
    const byId = moduleCatalog.find((m) => String(m.id).trim() === moduleKey);
    if (byId?.id) {
      filters.moduleId = String(byId.id).trim();
    } else {
      const byName = moduleCatalog.find((m) => String(m.name ?? "").trim() === moduleKey);
      if (byName?.id) {
        filters.moduleId = String(byName.id).trim();
      }
    }
  }

  if (uiFilters?.billingType && uiFilters.billingType !== "Both") {
    filters.billingType = uiFilters.billingType;
  }

  const dr = uiFilters?.dateRange || {};
  const start = toYmd(dr.startDate);
  const end = toYmd(dr.endDate);
  if (start && end) {
    filters.startDate = start;
    filters.endDate = end;
  }

  return filters;
}

/**
 * Extract presigned file URL from POST /billing/exports response.
 *
 * Typical shape:
 * ```json
 * {
 *   "success": true,
 *   "data": {
 *     "fileUrl": "https://....amazonaws.com/.../file.xlsx?X-Amz-..."
 *   }
 * }
 * ```
 */
export function getBillingExportFileUrlFromResponse(payload) {
  if (payload == null) return null;
  const url =
    payload.data?.fileUrl ??
    payload.data?.data?.fileUrl ??
    payload.data?.url ??
    payload.data?.data?.url ??
    payload.fileUrl ??
    payload.url;
  return typeof url === "string" && url.trim() !== "" ? url.trim() : null;
}

/** False when API returns `{ success: false, ... }` with HTTP 200 */
export function isBillingExportResponseSuccess(payload) {
  if (payload == null) return true;
  if (typeof payload.success === "boolean") return payload.success;
  return true;
}

export function getBillingExportMessageFromResponse(payload) {
  if (payload == null) return null;
  const msg =
    payload.data?.message ??
    payload.message ??
    payload.data?.data?.message ??
    (typeof payload.data === "string" ? payload.data : null);
  return typeof msg === "string" && msg.trim() !== "" ? msg.trim() : null;
}

/** Export job id from POST /billing/exports — prefer download via GET /billing/exports/:id/download */
export function getBillingExportIdFromResponse(payload) {
  if (payload == null) return null;
  const id = payload.data?.id ?? payload.id ?? payload.data?.exportId ?? payload.exportId;
  if (id == null) return null;
  const s = String(id).trim();
  return s !== "" ? s : null;
}

export function getBillingExportFileNameFromResponse(payload) {
  if (payload == null) return null;
  const n = payload.data?.fileName ?? payload.fileName;
  return typeof n === "string" && n.trim() !== "" ? n.trim() : null;
}
